import { faCheck, faCopy } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  cn,
  FieldError,
  Input,
  SettingsCard,
} from "@scribenote/brian-design-system";
import { formatCountdown } from "@utils/format-date";
import { useState } from "react";

import { usePairingCode } from "./usePairingCode";

export const PairingCodeCard = () => {
  const { code, secondsLeft, loading, error, expired, generate } =
    usePairingCode();
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState("");

  const generateLabel = loading
    ? "Generating"
    : code === null
      ? "Generate pairing code"
      : "Generate new code";

  const handleCopy = () => {
    if (!code) return;
    void navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard can be blocked; the code is on screen regardless.
      });
  };

  return (
    <SettingsCard
      title="Pairing code"
      subtitle="On the clinic computer, run the connector and enter this code."
      data-testid="connector-pairing-card"
    >
      <div className="flex flex-col gap-4">
        {code && (
          <div className="flex flex-col items-center gap-3 py-2">
            <div
              data-testid="connector-pairing-code"
              className={cn(
                "font-mono text-5xl font-semibold tracking-[0.35em]",
                expired ? "text-faint line-through" : "text-ink",
              )}
            >
              {code}
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={expired}
                data-testid="connector-copy-code"
              >
                <FontAwesomeIcon
                  icon={copied ? faCheck : faCopy}
                  aria-hidden="true"
                />
                {copied ? "Copied" : "Copy"}
              </Button>

              {expired ? (
                <span
                  data-testid="connector-code-expired"
                  className="text-sm font-medium text-ink-stopped"
                >
                  Code expired
                </span>
              ) : (
                <span
                  data-testid="connector-code-countdown"
                  className="text-sm text-annotation"
                  aria-live="polite"
                >
                  Expires in{" "}
                  <span className="font-mono font-medium text-ink tabular-nums">
                    {formatCountdown(secondsLeft)}
                  </span>
                </span>
              )}
            </div>
          </div>
        )}

        {error && (
          <FieldError data-testid="connector-pairing-error">{error}</FieldError>
        )}

        <Input
          label="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={loading}
          placeholder="e.g. Reception"
          data-testid="connector-pairing-name"
        />
        <Button
          onClick={() => generate(name.trim() || undefined)}
          busy={loading}
          data-testid="connector-generate-code"
        >
          {generateLabel}
        </Button>
      </div>
    </SettingsCard>
  );
};
